/**
 * URL Utility Functions
 * Handles URL parameter validation and sanitization
 * Supports multi-chain routing: /chain/:chainName/:address
 */

import { validateAndDetectChain, isChainSupported, getEnabledChains, validateAddress } from './chainUtils';
import { normalizeAddress } from './addressUtils';

/**
 * Validate if a string is a valid Ethereum address
 * @param {string} address - Address to validate
 * @returns {boolean} True if valid Ethereum address
 * @deprecated Use validateAndDetectChain from chainUtils instead
 */
export const isValidEthereumAddress = (address) => {
  if (!address || typeof address !== 'string') return false;
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

/**
 * Parse URL path to extract chain and address
 * Format: /chain/:chainName/:address
 * @returns {{ chain: string|null, address: string|null, error: string|null }} Parsed chain and address with optional error
 */
export const parseURLPath = () => {
  try {
    const path = window.location.pathname;
    const parts = path.split('/').filter(Boolean); // Remove empty strings

    // Format: /chain/:chainName/:address
    if (parts.length >= 3 && parts[0] === 'chain') {
      const [, chain, address] = parts;

      // Validate chain is supported in the registry
      if (!isChainSupported(chain)) {
        return { chain, address, error: `We are sorry chain "${chain}" is not supported yet. If you have a strong need for it, please send email to support@coincommons.io, we will try our best to support you. Thanks for your patience with us!` };
      }

      // Check if chain is enabled
      const enabledChains = getEnabledChains();
      const enabledChainIds = enabledChains.map(c => c.id);
      if (!enabledChainIds.includes(chain)) {
        const enabledNames = enabledChains.map(c => c.name).join(', ');
        return { chain, address, error: `${chain} is not currently available. Currently available: ${enabledNames}` };
      }

      // Validate address format for the specified chain (not auto-detect)
      const isValid = validateAddress(address, chain);
      if (!isValid) {
        const chainName = chain.charAt(0).toUpperCase() + chain.slice(1);
        return { chain, address, error: `Invalid ${chainName} address format` };
      }

      // Normalize address based on chain-specific rules
      const normalizedAddress = normalizeAddress(address);

      return { chain, address: normalizedAddress, error: null };
    }

    return { chain: null, address: null, error: null };
  } catch (error) {
    console.error('Error parsing URL path:', error);
    return { chain: null, address: null, error: 'Error parsing URL' };
  }
};

/**
 * Safely get address parameter from URL (multi-chain aware)
 * @returns {string|null} Validated address or null
 */
export const getAddressFromURL = () => {
  const { address } = parseURLPath();
  return address;
};

/**
 * Get chain from URL
 * @returns {string|null} Chain ID or null
 */
export const getChainFromURL = () => {
  const { chain } = parseURLPath();
  return chain;
};

/**
 * Safely get comment highlight ID from URL
 * @returns {string|null} Comment ID or null
 */
export const getHighlightCommentFromURL = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const commentId = urlParams.get('highlightComment');

    // Basic validation - should be alphanumeric
    if (commentId && /^[a-zA-Z0-9_-]+$/.test(commentId)) {
      return commentId;
    }
    return null;
  } catch (error) {
    console.error('Error reading URL parameters:', error);
    return null;
  }
};

/**
 * Safely update URL without navigation (multi-chain aware)
 * Uses path-based routing: /chain/:chainName/:address
 * @param {Object} params - Parameters to set/update
 * @param {string} params.address - Optional address
 * @param {string} params.chain - Optional chain
 * @param {boolean} replace - Use replaceState instead of pushState
 */
export const updateURL = (params = {}, replace = false) => {
  try {
    const url = new URL(window.location);

    // Auto-detect chain if address provided without chain
    if (params.address && !params.chain) {
      const validation = validateAndDetectChain(params.address);
      if (validation.valid) {
        params.chain = validation.chain;
      } else {
        console.error('Invalid address for URL update:', params.address);
        return;
      }
    }

    // Normalize address based on chain-specific rules
    if (params.address && params.chain) {
      params.address = params.chain === 'ethereum' ? params.address.toLowerCase() : params.address;
    }

    // Update path for chain and address
    if (params.chain && params.address) {
      url.pathname = `/chain/${params.chain}/${params.address}`;
    } else if (params.address === null || params.chain === null) {
      // Clear path when address or chain is null
      url.pathname = '/';
    }

    // Handle other query parameters (like highlightComment)
    Object.entries(params).forEach(([key, value]) => {
      if (key === 'address' || key === 'chain') {
        return; // Already handled in path
      }

      if (value === null || value === undefined) {
        url.searchParams.delete(key);
      } else {
        // Basic XSS prevention - only allow alphanumeric and basic chars
        const sanitizedValue = String(value).replace(/[<>'"]/g, '');
        url.searchParams.set(key, sanitizedValue);
      }
    });

    if (replace) {
      window.history.replaceState({}, '', url);
    } else {
      window.history.pushState({}, '', url);
    }
  } catch (error) {
    console.error('Error updating URL:', error);
  }
};

/**
 * Clear all URL parameters and path
 */
export const clearURL = () => {
  try {
    window.history.pushState({}, '', '/');
  } catch (error) {
    console.error('Error clearing URL:', error);
  }
};

/**
 * Build coin URL for href attribute
 * @param {string} coinId - CoinGecko coin ID
 * @param {string} [highlightComment] - Optional comment ID to highlight
 * @returns {string} URL path like /coin/bitcoin?highlightComment=xxx
 */
export const buildCoinUrl = (coinId, highlightComment = null) => {
  try {
    let url = `/coin/${encodeURIComponent(coinId)}`;
    if (highlightComment && /^[a-zA-Z0-9_-]+$/.test(highlightComment)) {
      url += `?highlightComment=${highlightComment}`;
    }
    return url;
  } catch (error) {
    console.error('Error building coin URL:', error);
    return `/coin/${coinId}`;
  }
};

/**
 * Build address URL for href attribute with auto-detected chain
 * @param {string} address - Crypto address
 * @param {string} [chainHint] - Optional chain hint (if known)
 * @param {string} [highlightComment] - Optional comment ID to highlight
 * @returns {string} URL path like /chain/ethereum/0x...
 */
export const buildAddressUrl = (address, chainHint = null, highlightComment = null) => {
  try {
    let basePath;

    // If chain hint provided and valid, use it
    if (chainHint && isChainSupported(chainHint)) {
      const normalizedAddress = chainHint === 'ethereum' ? address.toLowerCase() : address;
      basePath = `/chain/${chainHint}/${normalizedAddress}`;
    } else {
      // Otherwise auto-detect
      const validation = validateAndDetectChain(address);
      if (validation.valid && validation.chain) {
        const normalizedAddress = validation.chain === 'ethereum' ? address.toLowerCase() : address;
        basePath = `/chain/${validation.chain}/${normalizedAddress}`;
      } else {
        // Fallback to ethereum (most common)
        basePath = `/chain/ethereum/${address.toLowerCase()}`;
      }
    }

    // Add highlight comment query param if provided
    if (highlightComment && /^[a-zA-Z0-9_-]+$/.test(highlightComment)) {
      return `${basePath}?highlightComment=${highlightComment}`;
    }

    return basePath;
  } catch (error) {
    console.error('Error building address URL:', error);
    return `/chain/ethereum/${address.toLowerCase()}`;
  }
};
